// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, CircularProgress, Dialog, DialogContent } from '@mui/material';
import { Heading } from '@shared/ui-kit/components';
import {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useState,
} from 'react';
import { type AvailableOptions, OptionsSelector } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';

export interface PopupSelectorProps extends PropsWithChildren {
  readonly title: string | ReactNode;
  readonly options: AvailableOptions;
  readonly onSave: (options: string[]) => void | Promise<void>;
  readonly placeholder?: string;
  readonly showPopup?: boolean;
  readonly primaryButtonLabel?: string;
  readonly secondaryButtonLabel?: string;
  readonly testId?: string;
  readonly initialSelectedOptions?: string[];
  readonly description?: string;
}

export const PopupSelector: FC<PopupSelectorProps> = ({
  children,
  title,
  options,
  placeholder,
  showPopup = true,
  primaryButtonLabel,
  secondaryButtonLabel,
  onSave,
  testId = 'popup-selector',
  initialSelectedOptions = [],
  description,
}) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    initialSelectedOptions,
  );

  const handleSaveOptions = useCallback(async () => {
    setLoading(true);
    await onSave(selectedOptions);
    setLoading(false);
    setIsOpen(false);
  }, [onSave, selectedOptions]);

  return (
    <>
      <div
        className="inline-flex cursor-pointer"
        onClick={e => {
          e.stopPropagation();
          showPopup && setIsOpen(true);
        }}
      >
        {children}
      </div>
      <Dialog open={isOpen} data-testid={testId} onClick={e => e.stopPropagation()}>
        <DialogContent classes={{ root: '!p-10 w-[300px]' }}>
          <Heading variant="h2" title={title} className="!flex justify-center" />
          <div className="mt-4">
            {description && (
              <p className="mb-4 text-center text-subtitle">{description}</p>
            )}
            <OptionsSelector
              optionsAvailable={options}
              onChangeValueSelected={setSelectedOptions}
              optionSelected={selectedOptions}
              defaultText={
                placeholder ??
                (t('common.select-option', { ns: 'common' }) as string)
              }
              selectType="gray"
              checkbox
              multiple
            />

            <div className="mt-8 grid grid-cols-2 items-center gap-8">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setIsOpen(false)}
              >
                {secondaryButtonLabel ?? t('common.cancel', { ns: 'common' })}
              </Button>

              {loading ? (
                <CircularProgress
                  color="secondary"
                  size={24}
                  thickness={4}
                  className="justify-self-center"
                />
              ) : (
                <Button
                  color="secondary"
                  onClick={handleSaveOptions}
                  data-testid="save-button"
                >
                  {primaryButtonLabel ?? t('common.save', { ns: 'common' })}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
